import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Operator } from "../models/Operator";
import {
	removeOperator,
	upsertOperator,
} from "../repositories/OperatorsRepository";
import { OPERATORS_QUERY_KEYS } from "./useOperators";

interface MutationOptions {
	onSuccess?: (data?: Operator) => void;
	onError?: (error: Error) => void;
}

export const useUpsertOperator = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (operator: Operator) => {
			return await upsertOperator(operator);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: OPERATORS_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertOperator: mutation.mutate,
		upsertOperatorAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteOperator = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeOperator(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: OPERATORS_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteOperator: mutation.mutate,
		deleteOperatorAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
